import { CircleCheckFilledIcon, FlagIcon, WarningFilledIcon } from "@xola/icons";
import { Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { getQuestionnaireFieldCount, getQuestionnaireStatusIconColor, isExperienceProduct } from "common";
import React from "react";
import { Form, Guest, Product } from "types";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { QuestionnaireModal } from "../../Questionnaire/QuestionnaireModal";
import { OnChangeFunction, QuestionnaireNameField } from "./QuestionnaireNameField";
import { useVisibility } from "@/hooks/useVisibility";
import { hasUniqueGuestIdentification } from "@/models/Experience";
import { getAnswerCount, getGuestIndex, hasAddonLimitError } from "@/models/Guest";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";

interface GuestQuestionnaireItemProps {
    form?: Form;
    guest: Guest;
    product: Product;
    mainGuestVisible?: boolean;
    onChange: OnChangeFunction;
    onMainGuestChange: (mainGuest: Guest) => void;
    disableRequiredFields: boolean;
}

export const GuestQuestionnaireItem = ({
    form,
    guest,
    mainGuestVisible = true,
    product,
    onChange,
    onMainGuestChange,
    disableRequiredFields,
}: GuestQuestionnaireItemProps) => {
    const isExperience = isExperienceProduct(product);
    const isAttendeeUnique = isExperience ? hasUniqueGuestIdentification(product) : true;
    const catalogItems = isExperience ? product.catalog?.items ?? [] : [];
    const primaryGuest = useCreatePurchaseStore((state) => state.getPrimaryGuest());
    const [isModalOpen, { show: showModal, hide: hideModal }] = useVisibility();
    const guestAnswerCount = getAnswerCount(guest, form);
    const isPrimary = guest.id === primaryGuest?.id;
    const totalQuestions = form ? getQuestionnaireFieldCount(form, isPrimary) : 0;
    const safeTotalQuestions = typeof totalQuestions === "number" ? totalQuestions : 0;
    const itemRestrictionErrors = useCreatePurchaseStore((state) => {
        return state.getAddOnRestrictionErrors(state.activeItemIndex);
    });
    // Indexed in the item's guests, not the sorted list shown here
    const guestIndex = useCreatePurchaseStore((state) => getGuestIndex(state.getActiveItem()?.guests, guest));
    const isOverAddonLimit = hasAddonLimitError(guest, guestIndex, itemRestrictionErrors, catalogItems);

    return (
        <div
            className="flex items-center space-x-4 rounded border border-gray-lighter p-4"
            data-testid="guest-questionnaire-list-item"
        >
            {mainGuestVisible && (
                <div className="flex items-center">
                    <Tooltip content="Main attendee">
                        <div
                            className={clsx(
                                "rounded border py-2 px-3",
                                isPrimary
                                    ? "border-blue bg-primary-lighter"
                                    : "cursor-pointer border-gray-light hover:border-blue hover:bg-primary-lighter",
                            )}
                            onClick={() => onMainGuestChange(guest)}
                        >
                            <FlagIcon size="medium" className="leading-6" />
                        </div>
                    </Tooltip>
                </div>
            )}

            <QuestionnaireNameField
                guest={guest}
                product={product}
                disableRequiredFields={disableRequiredFields}
                onChange={(updated) => onChange(updated, true)}
            />

            {form && isAttendeeUnique && safeTotalQuestions > 0 ? (
                <SecondaryButton
                    className="no-translate font-bold leading-4"
                    icon={<Icon isOverAddonLimit={isOverAddonLimit} form={form} guest={guest} />}
                    data-testid="guest-questionnaire-list-answers-button"
                    onClick={showModal}
                >
                    {guestAnswerCount}/{totalQuestions}
                </SecondaryButton>
            ) : null}

            {form ? (
                <QuestionnaireModal
                    isOpen={isModalOpen}
                    form={form}
                    guest={guest}
                    disableRequiredFields={disableRequiredFields}
                    onClose={hideModal}
                    onSubmit={(updated) => onChange(updated, false)}
                />
            ) : null}
        </div>
    );
};

interface IconProps {
    isOverAddonLimit: boolean;
    form: Form;
    guest: Guest;
}

const Icon = ({ isOverAddonLimit, form, guest }: IconProps) => {
    const color = isOverAddonLimit ? "danger" : getQuestionnaireStatusIconColor(form, guest);

    return color === "success" ? (
        <CircleCheckFilledIcon size="medium" className="text-success" />
    ) : (
        <WarningFilledIcon className={`text-${color}`} size="medium" />
    );
};
